/**
 * Pinia store de preferências de acessibilidade.
 *
 * Persiste em localStorage (não no vault — preferências de UI não são
 * parte do modelo do aprendiz, CL-07). No primeiro boot (sem prefs
 * salvas), detecta `prefers-*` do OS para calibrar defaults (P-UX-04).
 *
 * Constitucional:
 * - CL-07: preferências de UI ficam em localStorage, separadas do vault.
 * - P-UX-01: tudo opt-in, defaults sensatos que seguem o OS.
 * - P-UX-04: acessibilidade first; OS é a fonte de verdade quando o
 *   usuário ainda não expressou preferência.
 * - Princípio X: zero julgamento; todas as escolhas são legítimas.
 */
import { defineStore } from "pinia";
import { ref } from "vue";
import {
  DEFAULT_PREFERENCES,
  type AccessibilityPreferences,
  type ColorMode,
  type MotionMode,
  type ContrastMode,
} from "./types";

const STORAGE_KEY = "hlos.a11y.preferences.v1";
const FIRST_RUN_KEY = "hlos.a11y.firstRun.done";

function detectOSPreferences(): Partial<AccessibilityPreferences> {
  if (typeof window === "undefined" || !window.matchMedia) return {};
  const out: Partial<AccessibilityPreferences> = {};
  // prefers-reduced-motion → motion=reduced
  try {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      out.motionMode = "reduced" as MotionMode;
    }
  } catch {
    // ignore
  }
  // prefers-color-scheme → colorMode
  try {
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      out.colorMode = "dark" as ColorMode;
    } else if (window.matchMedia("(prefers-color-scheme: light)").matches) {
      out.colorMode = "light" as ColorMode;
    }
  } catch {
    // ignore
  }
  // prefers-contrast: more → contrastMode=high
  try {
    if (window.matchMedia("(prefers-contrast: more)").matches) {
      out.contrastMode = "high" as ContrastMode;
    }
  } catch {
    // ignore
  }
  return out;
}

function isFirstRun(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(FIRST_RUN_KEY) !== "1";
}

function markFirstRunDone(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(FIRST_RUN_KEY, "1");
}

function loadFromStorage(): AccessibilityPreferences {
  if (typeof localStorage === "undefined") return { ...DEFAULT_PREFERENCES };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // Primeiro boot: aplicar detecção do OS sobre os defaults
      const detected = detectOSPreferences();
      return { ...DEFAULT_PREFERENCES, ...detected };
    }
    const parsed = JSON.parse(raw) as Partial<AccessibilityPreferences>;
    return { ...DEFAULT_PREFERENCES, ...parsed };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

function applyToDocument(prefs: AccessibilityPreferences): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-hlos-font-size", prefs.fontSize);
  root.setAttribute("data-hlos-font-family", prefs.fontFamily);
  root.setAttribute("data-hlos-color-mode", prefs.colorMode);
  root.setAttribute("data-hlos-contrast", prefs.contrastMode);
  root.setAttribute("data-hlos-colorblind", prefs.colorblindMode);
  root.setAttribute("data-hlos-motion", prefs.motionMode);
  root.setAttribute("data-hlos-slow", prefs.slowMode);
  root.setAttribute("data-hlos-voice-in", String(prefs.voiceInputEnabled));
  root.setAttribute("data-hlos-voice-out", String(prefs.voiceOutputEnabled));
}

export const useAccessibilityStore = defineStore("accessibility", () => {
  const preferences = ref<AccessibilityPreferences>(loadFromStorage());

  function setPreferences(next: Partial<AccessibilityPreferences>): void {
    preferences.value = { ...preferences.value, ...next };
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences.value));
    }
    applyToDocument(preferences.value);
  }

  function resetToDefaults(): void {
    preferences.value = { ...DEFAULT_PREFERENCES };
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences.value));
    }
    applyToDocument(preferences.value);
  }

  function init(): void {
    if (isFirstRun()) {
      // Persistir defaults detectados para que sejam estáveis
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences.value));
      }
      markFirstRunDone();
    }
    applyToDocument(preferences.value);
  }

  return {
    preferences,
    setPreferences,
    resetToDefaults,
    init,
  };
});
